import React, { useEffect, useRef, useState } from "react";
import { Col, Form, InputGroup, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstLiveTable from "../../components/DstTable/DstLiveTable";
import ListerSearch from "../../components/Lister/ListerSearch/ListerSearch";
import MultiFilterDropdown from "../../components/MultiFilterDropdown/MultiFilterDropdown";
import { useSession } from "../../contexts/SessionContext";
import { PermissionComponent } from "../../services/PermissionGuard";
import { formatDateHumanly, getLocalHourRange } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { Animator, Author, Meeting, MeetingTypes, Program } from "../Meetings/Meetings.d";
import { computeDay, fetchMeetingTypes, fetchResources } from "../Meetings/Meetings.function";
import MeetingCreationModal from "../Meetings/components/CreationModal/CreationModal";
import { generateLatestMeetingUrl } from "./MeetingLatest.function";

const MeetingLatest = () => {
  const { language } = useSession();
  const navigate = useNavigate();
  const defaultCreatedAfter = useRef(computeDay(-7));
  const [loading, setLoading] = useState({
    staff: true,
    types: true,
  });

  const [parameters, setParameters] = useState<{
    authors: string[];
    animators: string[];
    programs: string[];
    meetingTypes: string[];
    showUnassigned: boolean;
    showDetailedSearch: boolean;
    createdAfter: string | null;
    createdBefore: string | null;
  }>({
    authors: [],
    animators: [],
    programs: [],
    meetingTypes: [],
    showUnassigned: false,
    showDetailedSearch: false,
    createdAfter: defaultCreatedAfter.current,
    createdBefore: null,
  });
  const [tempCreatedAfter, setTempCreatedAfter] = useState<string | null>(defaultCreatedAfter.current);
  const [tempCreatedBefore, setTempCreatedBefore] = useState<string | null>(null);
  const [authors, setAuthors] = useState<Author[]>([]);
  const [animators, setAnimators] = useState<Animator[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [meetingTypes, setMeetingTypes] = useState<MeetingTypes[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showModalCreation, setShowModalCreation] = useState(false);

  useEffect(() => {
    fetchResources(setAuthors, setAnimators, setPrograms, setLoading, language);
    fetchMeetingTypes(setMeetingTypes, setLoading, language);
  }, [language]);

  const getColumns = () => {
    const baseColumns = [
      {
        key: "dt_created",
        name: translate(language, "PAGES.MEETING_LATEST.TABLE_HEADER.CREATED_DATE"),
        sortable: true,
        render: (item: Meeting) => formatDateHumanly(language, item.dt_created, true),
      },
      {
        key: "dt_start",
        name: translate(language, "PAGES.MEETING_LATEST.TABLE_HEADER.START_DATE"),
        render: (item: Meeting) => formatDateHumanly(language, item.dt_start),
        sortable: true,
      },
      {
        key: "hours_range",
        name: translate(language, "PAGES.MEETING_LATEST.TABLE_HEADER.TIME"),
        render: (item: Meeting) => getLocalHourRange(item.hours_range, item.dt_start),
      },
      {
        key: "name",
        name: translate(language, "PAGES.MEETING_LATEST.TABLE_HEADER.MEETING_NAME"),
        sortable: true,
        render: (item: Meeting) => item.name,
      },
      {
        key: "type_name",
        name: translate(language, "PAGES.MEETING_LATEST.TABLE_HEADER.MEETING_TYPE"),
        render: (item: Meeting) => item.type.name,
      },
      {
        key: "author",
        name: translate(language, "PAGES.MEETING_LATEST.TABLE_HEADER.ORGANIZER"),
        render: (item: Meeting) => (
          <span
            className="text-primary clickable"
            onClick={(event) => {
              event.stopPropagation();
              completeNavigate(event, navigate, `/user/${item.author.id}`);
            }}
          >
            {item.author.full_name}
          </span>
        ),
      },
      {
        key: "animators",
        name: translate(language, "PAGES.MEETING_LATEST.TABLE_HEADER.ANIMATORS"),
        render: (item: Meeting) => (
          <div>
            {item.animators?.length ? (
              item.animators.map((animator, index) => (
                <div key={animator.id}>
                  <span
                    className="text-primary clickable"
                    onClick={(event) => {
                      event.stopPropagation();
                      completeNavigate(event, navigate, `/user/${animator.id}`);
                    }}
                  >
                    {animator.full_name}
                  </span>
                  {index < item.animators!.length - 1 && ", "}
                </div>
              ))
            ) : (
              <span className="text-muted">{translate(language, "PAGES.MEETING_LATEST.TABLE_BODY.NO_ANIMATORS")}</span>
            )}
          </div>
        ),
      },
    ];

    if (parameters.showDetailedSearch) {
      const detailedColumns = [
        {
          key: "dt_updated",
          name: translate(language, "PAGES.MEETING_LATEST.TABLE_HEADER.UPDATED_DATE"),
          sortable: true,
          render: (item: Meeting) =>
            item.dt_updated ? formatDateHumanly(language, item.dt_updated) : <span className="text-muted">N/A</span>,
        },
        {
          key: "campus_names",
          name: translate(language, "PAGES.MEETING_LATEST.TABLE_HEADER.CAMPUS"),
          render: (item: Meeting) =>
            item.campus_names?.length ? (
              item.campus_names.join(", ")
            ) : (
              <span className="text-muted">{translate(language, "PAGES.MEETING_LATEST.TABLE_BODY.NO_CAMPUS")}</span>
            ),
        },
      ];
      return [...baseColumns, ...detailedColumns];
    }

    return baseColumns;
  };

  return (
    <div className="p-3" id="meeting-latest">
      <Row className="d-flex justify-content-between mb-3">
        <Col xl className="d-flex align-items-center">
          <div className="d-flex align-items-center me-3">
            <h1 className="mb-0 h2">
              <span className="me-1 text-nowrap">{translate(language, "PAGES.MEETING_LATEST.TITLE")}</span>
            </h1>
            <OverlayTrigger
              key="overlay-trigger-latest-meeting-info"
              placement="bottom"
              overlay={
                <Tooltip aria-label="description">
                  {translate(language, "PAGES.MEETING_LATEST.OVERLAY_DESCRIPTION")}
                </Tooltip>
              }
            >
              <span className="clickable dst-icon-info-circle ms-2" />
            </OverlayTrigger>
          </div>
          <InputGroup>
            <ListerSearch setSearchQuery={setSearchQuery} timeDelay={500} showSearchIcon />
          </InputGroup>
        </Col>
        <Col xl className="d-flex align-items-center justify-content-xl-end mt-3 md-xl-0">
          <div className="white-background rounded me-2 p-2 s-bold align-content-center">
            <Form.Check
              type="checkbox"
              id="unassigned-checkbox"
              label={translate(language, "PAGES.MEETING_LATEST.FILTERS.UNASSIGNED_MEETINGS")}
              checked={parameters.showUnassigned}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setParameters((parameters) => ({ ...parameters, showUnassigned: event.target.checked }))
              }
            />
          </div>
          <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/meetings/batch" }]}>
            <DstButton
              btnClass="me-2"
              variant="light"
              value={translate(language, "PAGES.MEETINGS.BUTTONS.IMPORT_CSV")}
              btnImageBefore={<span className="me-2 dst-icon-download-03" />}
              clickFunction={() => navigate("/meetings/import")}
            />
          </PermissionComponent>
          <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/meetings" }]}>
            <DstButton
              value={translate(language, "PAGES.MEETINGS.BUTTONS.CREATE_MEETING")}
              btnImageBefore={<span className="me-2 dst-icon-plus" />}
              clickFunction={() => setShowModalCreation(true)}
            />
          </PermissionComponent>
          <MeetingCreationModal
            showCreationModal={showModalCreation}
            setShowCreationModal={setShowModalCreation}
            language={language}
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={8} className="d-flex flex-wrap align-items-center gap-2">
          <div className="w-fit mt-1 d-flex align-items-center" style={{ height: 50 }}>
            <DstButton
              btnClass="h-100 d-flex align-items-center"
              btnImageBefore={
                <Form.Check
                  type="switch"
                  className="check-box s-bold me-2"
                  id="detailed-search-switch"
                  checked={parameters.showDetailedSearch}
                />
              }
              value={translate(language, "PAGES.MEETING_LATEST.FILTERS.DETAILED_SEARCH")}
              variant="light"
              btnHeight="50px"
              btnWidth="150px"
              clickFunction={() =>
                setParameters((parameters) => ({
                  ...parameters,
                  showDetailedSearch: !parameters.showDetailedSearch,
                }))
              }
            />
          </div>
          <div className="w-fit mt-1">
            <MultiFilterDropdown
              inputTitle={translate(language, "PAGES.MEETING_LATEST.FILTERS.MEETING_TYPE")}
              tags={meetingTypes.map((meetingType) => ({
                value: meetingType.id,
                display: meetingType.name,
              }))}
              onSelectTags={(tags) =>
                setParameters((parameters) => ({ ...parameters, meetingTypes: tags.map(String) }))
              }
              defaultValue={parameters.meetingTypes}
              buttonWidth="120px"
              showSearchBar
              language={language}
              mock={loading.types}
            />
          </div>
          <div className="w-fit mt-1">
            <MultiFilterDropdown
              inputTitle={translate(language, "PAGES.MEETING_LATEST.FILTERS.ORGANIZER")}
              tags={authors.map((author) => ({
                value: author.id,
                display: author.full_name,
              }))}
              onSelectTags={(tags) =>
                setParameters((parameters: any) => ({ ...parameters, authors: tags.map(Number) }))
              }
              defaultValue={parameters.authors}
              buttonWidth="120px"
              showSearchBar
              language={language}
              mock={loading.staff}
            />
          </div>
          <div className="w-fit mt-1">
            <MultiFilterDropdown
              inputTitle={translate(language, "PAGES.MEETING_LATEST.FILTERS.ANIMATOR")}
              tags={animators.map((animator) => ({
                value: animator.staff_member_id,
                display: animator.full_name,
              }))}
              onSelectTags={(tags) =>
                setParameters((parameters: any) => ({ ...parameters, animators: tags.map(Number) }))
              }
              defaultValue={parameters.animators}
              buttonWidth="120px"
              showSearchBar
              language={language}
              mock={loading.staff}
            />
          </div>
          <div className="w-fit mt-1">
            <MultiFilterDropdown
              inputTitle={translate(language, "PAGES.MEETING_LATEST.FILTERS.PROGRAM")}
              tags={programs.map((program) => ({
                value: program.id,
                display: program.name,
              }))}
              onSelectTags={(tags) =>
                setParameters((parameters: any) => ({ ...parameters, programs: tags.map(Number) }))
              }
              defaultValue={parameters.programs}
              buttonWidth="120px"
              showSearchBar
              language={language}
              mock={loading.staff}
            />
          </div>
          {(parameters.meetingTypes.length > 0 ||
            parameters.authors.length > 0 ||
            parameters.animators.length > 0 ||
            parameters.programs.length > 0 ||
            parameters.showUnassigned ||
            parameters.showDetailedSearch ||
            (parameters.createdAfter && parameters.createdAfter !== defaultCreatedAfter.current) ||
            parameters.createdBefore) && (
            <div
              className="ms-1 clickable dark-gray-font text-decoration-underline xs-regular w-fit align-content-center"
              onClick={() => {
                setParameters((parameters) => ({
                  ...parameters,
                  meetingTypes: [],
                  authors: [],
                  animators: [],
                  programs: [],
                  showUnassigned: false,
                  showDetailedSearch: false,
                  createdAfter: defaultCreatedAfter.current,
                  createdBefore: null,
                }));
                setTempCreatedAfter(defaultCreatedAfter.current);
                setTempCreatedBefore(null);
              }}
            >
              {translate(language, "PAGES.MEETING_LATEST.FILTERS.CLEAR_FILTERS")}
            </div>
          )}
        </Col>
        <Col className="d-flex align-items-end justify-content-lg-end">
          <div className="me-2">
            <label htmlFor="created-after-date" className="form-label dark-gray-font xs-regular mb-1">
              {translate(language, "PAGES.MEETING_LATEST.FILTERS.CREATED_AFTER")}
            </label>
            <input
              type="date"
              id="created-after-date"
              className="form-control s-regular"
              value={tempCreatedAfter || ""}
              onChange={(event) => setTempCreatedAfter(event.target.value)}
            />
          </div>
          <div className="me-2">
            <label htmlFor="created-before-date" className="form-label dark-gray-font xs-regular mb-1">
              {translate(language, "PAGES.MEETING_LATEST.FILTERS.CREATED_BEFORE")}
            </label>
            <input
              type="date"
              id="created-before-date"
              className="form-control s-regular"
              value={tempCreatedBefore || ""}
              onChange={(event) => setTempCreatedBefore(event.target.value)}
            />
          </div>
          <DstButton
            clickFunction={() => {
              setParameters((parameters) => ({
                ...parameters,
                createdAfter: tempCreatedAfter,
                createdBefore: tempCreatedBefore,
              }));
            }}
            value=""
            btnClass="py-2"
            btnImageBefore={<span className="dst-icon-search-md" />}
            tooltip={translate(language, "PAGES.MEETING_LATEST.FILTERS.APPLY_DATES")}
          />
          <DstButton
            clickFunction={() => {
              setTempCreatedAfter(defaultCreatedAfter.current);
              setTempCreatedBefore(null);
              setParameters((parameters) => ({
                ...parameters,
                createdAfter: defaultCreatedAfter.current,
                createdBefore: null,
              }));
            }}
            value=""
            btnClass="py-2 ms-2"
            variant="secondary"
            btnImageBefore={<span className="dst-icon-eraser" />}
            tooltip={translate(language, "PAGES.MEETING_LATEST.FILTERS.RESET_DATES")}
          />
        </Col>
      </Row>
      <DstLiveTable
        remote={generateLatestMeetingUrl({
          meetingTypes: parameters.meetingTypes,
          authors: parameters.authors,
          animators: parameters.animators,
          programs: parameters.programs,
          showUnassigned: parameters.showUnassigned,
          showDetailedSearch: parameters.showDetailedSearch,
          searchQuery: searchQuery,
          createdAfter: parameters.createdAfter,
          createdBefore: parameters.createdBefore,
        })}
        remoteKey="meetings"
        columns={getColumns()}
        onRowClick={(event, item) => completeNavigate(event, navigate, `/meeting/${item.id}`)}
        itemsPerPage={20}
        pagination
        paginationAlwaysShowFooter
        paginationResizeOptions={[20, 50, 100]}
        defaultSortKey="dt_created"
        defaultSortOrder="desc"
      />
    </div>
  );
};

export default MeetingLatest;
